'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, EyeOff, Sun, Moon, Globe, Copy, CheckCheck, ArrowRight } from 'lucide-react';
import { useAppStore } from '@/lib/themeStore';
import { t } from '@/lib/i18n';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupForm {
  fullName: string;
  companyName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const DEMO_CREDENTIALS = [
  {
    role: 'tenant_owner' as const,
    roleAr: 'قائد الفريق',
    roleEn: 'Team Leader',
    email: 'n.qahtani@sahab.io',
    password: 'Sahab@2026',
  },
  {
    role: 'member' as const,
    roleAr: 'عضو الفريق',
    roleEn: 'Team Member',
    email: 'o.ghamdi@sahab.io',
    password: 'Sahab@2026',
  },
];

// Super admin credential shown separately — not mixed with tenant credentials
const SUPER_ADMIN_CREDENTIAL = {
  role: 'super_admin' as const,
  roleAr: 'المشرف العام',
  roleEn: 'Super Admin',
  email: 'admin@day-flow.cloud',
  password: 'DayFlow@2026',
};

// Reserved super admin emails — cannot be used for tenant signup
const SUPER_ADMIN_EMAILS = ['admin@day-flow.cloud'];

interface AuthPageClientProps {
  defaultTab?: 'login' | 'signup';
}

export default function AuthPageClient({ defaultTab = 'login' }: AuthPageClientProps) {
  const { locale, theme, toggleTheme, setLocale } = useAppStore();
  const { signIn } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Invite-link params: tenantId + role come from the invite URL
  const inviteTenantId = searchParams.get('tenantId');
  const inviteRole = searchParams.get('role') ?? 'member';
  const isInviteFlow = Boolean(inviteTenantId);

  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const isRTL = locale === 'ar';

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const signupForm = useForm<SignupForm>({
    defaultValues: { fullName: '', companyName: '', email: '', password: '', confirmPassword: '' },
  });

  const handleCopy = (text: string, field: string) => {
    // Safe clipboard: fallback for non-HTTPS or older browsers
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    } else {
      try {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      } catch {
        // silently ignore if copy not supported
      }
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseCredential = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    setAuthError('');
  };

  const onLogin = async (data: LoginForm) => {
    setIsLoading(true);
    setAuthError('');
    try {
      await signIn(data.email, data.password);
      // Determine redirect based on user role
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Block unverified users
        if (!user.email_confirmed_at) {
          await supabase.auth.signOut();
          setAuthError(
            locale === 'ar' ?'يرجى تأكيد بريدك الإلكتروني أولاً. تحقق من صندوق الوارد وانقر على رابط التأكيد.' :'Please verify your email first. Check your inbox and click the confirmation link.'
          );
          setIsLoading(false);
          return;
        }
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();
        const role = profile?.role || user.user_metadata?.role || 'member';
        if (role === 'super_admin') {
          router.push('/super-admin-dashboard');
        } else if (role === 'tenant_owner') {
          router.push('/tenant-owner-dashboard');
        } else {
          router.push('/daily-planner');
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.message === 'Invalid login credentials') {
        setAuthError(t('invalidCredentials', locale));
      } else {
        setAuthError(err.message || (locale === 'ar' ? 'فشل تسجيل الدخول. حاول مرة أخرى.' : 'Login failed. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignupForm) => {
    setIsLoading(true);
    setAuthError('');
    try {
      // Block reserved super admin emails from tenant signup
      if (SUPER_ADMIN_EMAILS.includes(data.email.toLowerCase().trim())) {
        setAuthError(
          locale === 'ar' ?'هذا البريد الإلكتروني محجوز ولا يمكن استخدامه للتسجيل.' :'This email address is reserved and cannot be used for registration.'
        );
        setIsLoading(false);
        return;
      }

      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://dayflow2144.builtwithrocket.new';

      // Determine role and tenant for this signup
      const signupRole = isInviteFlow ? inviteRole : 'tenant_owner';
      const signupTenantId = isInviteFlow ? inviteTenantId : undefined;

      // Build redirect URL (carries params for the auth/callback route)
      let redirectUrl = `${siteUrl}/auth/callback?full_name=${encodeURIComponent(data.fullName)}`;
      if (!isInviteFlow) {
        redirectUrl += `&company_name=${encodeURIComponent(data.companyName)}`;
      }
      if (signupTenantId) {
        redirectUrl += `&tenant_id=${encodeURIComponent(signupTenantId)}&role=${encodeURIComponent(signupRole)}`;
      }

      // Create the auth user — Supabase will send a confirmation email
      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            role: signupRole,
            company_name: isInviteFlow ? undefined : data.companyName,
            tenant_id: signupTenantId ?? undefined,
          },
          emailRedirectTo: redirectUrl,
        },
      });

      if (signupError) {
        if (
          signupError.message.includes('already registered') ||
          signupError.message.includes('already been registered')
        ) {
          setAuthError(
            locale === 'ar'
              ? isInviteFlow
                ? 'هذا البريد الإلكتروني مسجل مسبقاً. يرجى تسجيل الدخول للانضمام إلى الفريق.'
                : 'هذا البريد الإلكتروني مسجل مسبقاً. يرجى تسجيل الدخول.'
              : isInviteFlow
                ? 'This email is already registered. Please sign in to join the team.'
                : 'This email is already registered. Please sign in.'
          );
        } else {
          setAuthError(signupError.message);
        }
        return;
      }

      if (!authData?.user) {
        setAuthError(
          locale === 'ar' ? 'حدث خطأ أثناء إنشاء الحساب.' : 'Failed to create account.'
        );
        return;
      }

      // Show "check your email" success screen — do NOT sign in
      setSignupSuccess(true);
    } catch (err: any) {
      console.error('Signup error:', err);
      setAuthError(err.message || (locale === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    t('feature1', locale),
    t('feature2', locale),
    t('feature3', locale),
    t('feature4', locale),
  ];

  return (
    <div
      className="min-h-screen flex"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{ backgroundColor: 'var(--background)' }}
    >
      {/* Brand panel */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-10 xl:p-14 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1A1D27 0%, #0F1117 60%, #1A1030 100%)',
          borderInlineEnd: '1px solid var(--border)',
        }}
      >
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, var(--primary) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, var(--accent) 0%, transparent 50%)`,
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <span className="text-xl font-bold" style={{ color: 'var(--primary)' }}>DayFlow</span>
          </div>
          <div className="mb-8">
            <h1 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight" style={{ color: 'var(--foreground)' }}>
              {t('tagline', locale)}
            </h1>
            <p className="text-base" style={{ color: 'var(--muted-foreground)', lineHeight: 1.7 }}>
              {t('heroDesc', locale)}
            </p>
          </div>
          <div className="space-y-3">
            {features.map((feature, idx) => (
              <div key={`feature-${idx}`} className="flex items-center gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(108,99,255,0.2)' }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5L4 7L8 3" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-8">
          {[
            { value: '12+', labelAr: 'فريق نشط', labelEn: 'Active Workspaces' },
            { value: '2.9K', labelAr: 'SAR/شهر MRR', labelEn: 'SAR/mo MRR' },
            { value: '99.8%', labelAr: 'وقت التشغيل', labelEn: 'Uptime' },
          ].map((stat) => (
            <div key={`stat-${stat.value}`}>
              <div className="text-xl font-bold tabular-nums" style={{ color: 'var(--primary)' }}>{stat.value}</div>
              <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                {locale === 'ar' ? stat.labelAr : stat.labelEn}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2 lg:hidden">
            <AppLogo size={28} />
            <span className="font-bold text-sm" style={{ color: 'var(--primary)' }}>DayFlow</span>
          </div>
          <div className="ms-auto flex items-center gap-2">
            <button
              onClick={() => setLocale(locale === 'ar' ? 'en' : 'ar')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-muted"
              style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
            >
              <Globe size={14} />
              {locale === 'ar' ? 'EN' : 'AR'}
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-colors hover:bg-muted"
              style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}
            >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">
            <div className="flex rounded-xl p-1 mb-8" style={{ backgroundColor: 'var(--muted)' }}>
              {(['login', 'signup'] as const).map((tab) => (
                <button
                  key={`tab-${tab}`}
                  onClick={() => { setActiveTab(tab); setAuthError(''); }}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                  style={{
                    backgroundColor: activeTab === tab ? 'var(--card)' : 'transparent',
                    color: activeTab === tab ? 'var(--foreground)' : 'var(--muted-foreground)',
                    boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
                  }}
                >
                  {tab === 'login' ? t('login', locale) : t('signup', locale)}
                </button>
              ))}
            </div>

            {authError && (
              <div
                className="flex items-center gap-2 px-4 py-3 rounded-lg mb-4 text-sm"
                style={{ backgroundColor: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.2)' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {authError}
              </div>
            )}

            {activeTab === 'login' && (
              <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                    {t('email', locale)}
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder={locale === 'ar' ? 'example@company.sa' : 'you@company.com'}
                    {...loginForm.register('email', {
                      required: t('emailRequired', locale),
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('emailInvalid', locale) },
                    })}
                    dir="ltr"
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                      {loginForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                    {t('password', locale)}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input-field"
                      placeholder="••••••••"
                      {...loginForm.register('password', {
                        required: t('passwordRequired', locale),
                        minLength: { value: 8, message: t('passwordMin', locale) },
                      })}
                      dir="ltr"
                      style={{ paddingInlineEnd: '2.5rem' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute inset-y-0 end-3 flex items-center"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>
                      {loginForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...loginForm.register('rememberMe')} className="w-4 h-4 rounded accent-primary" />
                    <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{t('rememberMe', locale)}</span>
                  </label>
                  <Link href="/forgot-password" className="text-sm font-medium" style={{ color: 'var(--primary)' }}>
                    {t('forgotPassword', locale)}
                  </Link>
                </div>

                <button type="submit" disabled={isLoading} className="btn-primary w-full" style={{ height: '44px' }}>
                  {isLoading ? (
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                    </svg>
                  ) : (
                    <>{t('login', locale)}<ArrowRight size={16} /></>
                  )}
                </button>

                <p className="text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {t('noAccount', locale)}{' '}
                  <button type="button" onClick={() => setActiveTab('signup')} className="font-semibold" style={{ color: 'var(--primary)' }}>
                    {t('signupHere', locale)}
                  </button>
                </p>
              </form>
            )}

            {activeTab === 'signup' && !signupSuccess && (
              <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
                {/* Invite-flow banner */}
                {isInviteFlow && (
                  <div
                    className="flex items-start gap-2 px-4 py-3 rounded-lg text-sm"
                    style={{ backgroundColor: 'rgba(108,99,255,0.08)', border: '1px solid rgba(108,99,255,0.25)' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" className="flex-shrink-0 mt-0.5">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span style={{ color: 'var(--foreground)' }}>
                      {locale === 'ar'
                        ? 'أنت مدعو للانضمام إلى فريق. أنشئ حسابك للمتابعة.'
                        : "You've been invited to join a team. Create your account to continue."}
                    </span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>{t('fullName', locale)}</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder={locale === 'ar' ? 'الاسم الكامل' : 'Your full name'}
                    {...signupForm.register('fullName', { required: t('nameRequired', locale) })}
                  />
                  {signupForm.formState.errors.fullName && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{signupForm.formState.errors.fullName.message}</p>
                  )}
                </div>
                {/* Company Name — shown only when creating a new workspace, not on invites */}
                {!isInviteFlow && (
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>{t('companyName', locale)}</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder={locale === 'ar' ? 'اسم شركتك' : 'Your company name'}
                    {...signupForm.register('companyName', { required: !isInviteFlow ? t('companyRequired', locale) : false })}
                  />
                  {signupForm.formState.errors.companyName && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{signupForm.formState.errors.companyName.message}</p>
                  )}
                </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>{t('email', locale)}</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder={locale === 'ar' ? 'example@company.sa' : 'you@company.com'}
                    {...signupForm.register('email', {
                      required: t('emailRequired', locale),
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('emailInvalid', locale) },
                    })}
                    dir="ltr"
                  />
                  {signupForm.formState.errors.email && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{signupForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>{t('password', locale)}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="input-field"
                      placeholder="••••••••"
                      {...signupForm.register('password', {
                        required: t('passwordRequired', locale),
                        minLength: { value: 8, message: t('passwordMin', locale) },
                      })}
                      dir="ltr"
                      style={{ paddingInlineEnd: '2.5rem' }}
                    />
                    <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute inset-y-0 end-3 flex items-center" style={{ color: 'var(--muted-foreground)' }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{signupForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>{t('confirmPassword', locale)}</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className="input-field"
                      placeholder="••••••••"
                      {...signupForm.register('confirmPassword', {
                        required: t('passwordRequired', locale),
                        validate: (val) => val === signupForm.watch('password') || t('passwordMismatch', locale),
                      })}
                      dir="ltr"
                      style={{ paddingInlineEnd: '2.5rem' }}
                    />
                    <button type="button" onClick={() => setShowConfirmPassword((p) => !p)} className="absolute inset-y-0 end-3 flex items-center" style={{ color: 'var(--muted-foreground)' }}>
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirmPassword && (
                    <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{signupForm.formState.errors.confirmPassword.message}</p>
                  )}
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full" style={{ height: '44px' }}>
                  {isLoading ? (
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                    </svg>
                  ) : t('signup', locale)}
                </button>
                <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {locale === 'ar' ? 'بالتسجيل، أنت توافق على شروط الخدمة وسياسة الخصوصية' : 'By signing up, you agree to our Terms of Service and Privacy Policy'}
                </p>
                <p className="text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  {t('hasAccount', locale)}{' '}
                  <button type="button" onClick={() => setActiveTab('login')} className="font-semibold" style={{ color: 'var(--primary)' }}>
                    {t('loginHere', locale)}
                  </button>
                </p>
              </form>
            )}

            {activeTab === 'signup' && signupSuccess && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--success-bg)', color: 'var(--success)' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--foreground)' }}>
                  {locale === 'ar' ? 'تحقق من بريدك الإلكتروني!' : 'Check Your Email!'}
                </h3>
                <p className="text-sm mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  {locale === 'ar' ?'أرسلنا رابط تأكيد إلى بريدك الإلكتروني.' :'We sent a confirmation link to your email address.'}
                </p>
                <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                  {locale === 'ar' ?'انقر على الرابط في البريد لتفعيل حسابك والبدء في استخدام DayFlow.' :'Click the link in the email to activate your account and start using DayFlow.'}
                </p>
                <div
                  className="flex items-start gap-2 px-4 py-3 rounded-lg mb-6 text-sm text-start"
                  style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span style={{ color: 'var(--muted-foreground)' }}>
                    {locale === 'ar' ?'لم يصلك البريد؟ تحقق من مجلد الرسائل غير المرغوب فيها (Spam).' : "Didn't receive it? Check your spam or junk folder."}
                  </span>
                </div>
                <button
                  onClick={() => { setActiveTab('login'); setSignupSuccess(false); }}
                  className="btn-primary w-full"
                  style={{ height: '44px' }}
                >
                  {locale === 'ar' ? 'العودة إلى تسجيل الدخول' : 'Back to Sign In'}
                </button>
              </div>
            )}

            {activeTab === 'login' && (
              <div className="mt-6 rounded-xl p-4 border" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
                <p className="text-xs font-semibold mb-3 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                  {t('demoCredentials', locale)}
                </p>
                <div className="space-y-2">
                  {/* Super Admin — isolated section */}
                  {[SUPER_ADMIN_CREDENTIAL, ...DEMO_CREDENTIALS].map((cred) => (
                    <div
                      key={`cred-${cred.role}`}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg"
                      style={{
                        backgroundColor: 'var(--card)',
                        border: cred.role === 'super_admin' ?'1px solid rgba(108,99,255,0.4)' :'1px solid var(--border)',
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span
                            className="text-xs font-semibold px-1.5 py-0.5 rounded"
                            style={{
                              backgroundColor: cred.role === 'super_admin' ? 'rgba(108,99,255,0.15)' : 'rgba(34,211,238,0.15)',
                              color: cred.role === 'super_admin' ? 'var(--primary)' : 'var(--accent)',
                              fontSize: '10px',
                            }}
                          >
                            {locale === 'ar' ? cred.roleAr : cred.roleEn}
                          </span>
                          {cred.role === 'super_admin' && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: 'rgba(108,99,255,0.1)', color: 'var(--primary)', fontSize: '9px', fontWeight: 600 }}
                            >
                              {locale === 'ar' ? 'منفصل' : 'Isolated'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs truncate tabular-nums" style={{ color: 'var(--foreground)', direction: 'ltr' }}>
                          {cred.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleCopy(cred.email, `${cred.role}-email`)}
                          className="p-1.5 rounded hover:bg-muted transition-colors"
                          style={{ color: 'var(--muted-foreground)' }}
                          title="Copy email"
                        >
                          {copiedField === `${cred.role}-email` ? <CheckCheck size={13} /> : <Copy size={13} />}
                        </button>
                        <button
                          onClick={() => handleUseCredential(cred.email, cred.password)}
                          className="text-xs font-semibold px-2 py-1 rounded transition-all duration-150 active:scale-95"
                          style={{ backgroundColor: 'var(--primary)', color: '#fff' }}
                        >
                          {t('useCredentials', locale)}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}